// src/services/associationsServices.js
import axios from "axios";

const baseUrl = `${process.env.REACT_APP_API_URL}/api/associations`;

/* =========================
   Helpers
   ========================= */
// Récupère le token à chaque appel (utile après login/logout)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Construit une querystring propre
const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null) return;

    // Tableaux -> "a,b,c" (ex: categorie)
    if (Array.isArray(val)) {
      const joined = val
        .map((x) => String(x).trim())
        .filter(Boolean)
        .join(",");
      if (joined) q.append(key, joined);
      return;
    }

    // Booléens -> "true"/"false"
    if (typeof val === "boolean") {
      q.append(key, val ? "true" : "false");
      return;
    }

    // Autres types
    const str = String(val).trim();
    if (str !== "") q.append(key, str);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

// URL absolue (utile si /uploads est servi par le back)
export const toAbsoluteLogoUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  if (url.startsWith("/uploads/") || url.startsWith("uploads/")) {
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  }
  return url;
};

/* =========================
   API — Public
   ========================= */

// GET — Liste simple (actives, tri alpha) pour préchargement
export const getAssociations = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllAssociations`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Erreur lors du chargement des associations :", error);
    return [];
  }
};

/* =========================
   API — Admin
   ========================= */

// GET — Recherche + filtres + pagination
// params: { q, categorie: string[]|string, is_active, has_logo, with_gps, page, limit, sort, dir }
export const searchAssociations = async (params = {}) => {
  try {
    const qs = buildQuery(params);
    const { data } = await axios.get(`${baseUrl}/search${qs}`, {
      headers: authHeaders(),
    });

    // Normalisation du payload
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: Number.isFinite(data?.total) ? data.total : 0,
      page: Number.isFinite(data?.page) ? data.page : 1,
      limit: Number.isFinite(data?.limit) ? data.limit : 24,
      totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    };
  } catch (error) {
    console.error("Erreur recherche associations :", error);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

// GET — Alias explicite pour coller à la route/back
export const getAllAssociations = async () => {
  try {
    const { data } = await axios.get(`${baseUrl}/getAllAssociations`, {
      headers: authHeaders(),
    });
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Erreur lors du chargement des associations (getAllAssociations) :",
      error
    );
    return [];
  }
};

// GET — Détail
export const getAssociationById = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.get(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur chargement association :", error);
    return null;
  }
};

// POST — Création
export const createAssociation = async (payload) => {
  try {
    const { data } = await axios.post(`${baseUrl}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur création association :", error);
    return null;
  }
};

// PUT — Remplacement total
export const updateAssociation = async (id, payload) => {
  if (!id) return null;
  try {
    const { data } = await axios.put(`${baseUrl}/${id}`, payload, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur mise à jour association :", error);
    return null;
  }
};

// PATCH — Mise à jour partielle
export const patchAssociation = async (id, partial) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}`, partial, {
      headers: { "Content-Type": "application/json", ...authHeaders() },
    });
    return data || null;
  } catch (error) {
    console.error("Erreur patch association :", error);
    return null;
  }
};

// PATCH — Activer/Désactiver
export const toggleAssociationActive = async (id) => {
  if (!id) return null;
  try {
    const { data } = await axios.patch(`${baseUrl}/${id}/toggle`, null, {
      headers: authHeaders(),
    });
    return data || null;
  } catch (error) {
    console.error("Erreur toggle association :", error);
    return null;
  }
};

// DELETE — Suppression
export const deleteAssociation = async (id) => {
  if (!id) return false;
  try {
    await axios.delete(`${baseUrl}/${id}`, {
      headers: authHeaders(),
    });
    return true;
  } catch (error) {
    console.error("Erreur suppression association :", error);
    return false;
  }
};
